import React, { useState, useEffect, useCallback } from 'react';
import { FaChevronRight, FaChevronDown, FaFile } from 'react-icons/fa';

const WikiTreeNav = ({
    pages,
    selectedPageId,
    onSelect,
    storageKey = 'clientWikiTreeExpanded'
}) => {
    const [expandedNodes, setExpandedNodes] = useState(() => {
        const saved = localStorage.getItem(storageKey);
        return saved ? new Set(JSON.parse(saved)) : new Set();
    });

    useEffect(() => {
        localStorage.setItem(storageKey, JSON.stringify([...expandedNodes]));
    }, [expandedNodes, storageKey]);

    const buildTree = useCallback((flatPages) => {
        if (!flatPages || flatPages.length === 0) return [];

        const pageMap = new Map();
        flatPages.forEach(page => {
            pageMap.set(page.pageId, { ...page, children: [] });
        });

        const rootPages = [];
        flatPages.forEach(page => {
            const node = pageMap.get(page.pageId);
            if (!page.parentId) {
                rootPages.push(node);
            } else {
                const parent = pageMap.get(page.parentId);
                if (parent) {
                    parent.children.push(node);
                } else {
                    rootPages.push(node);
                }
            }
        });

        const sortByOrder = (nodes) => {
            nodes.sort((a, b) => {
                const orderA = typeof a.order === 'number' ? a.order : 0;
                const orderB = typeof b.order === 'number' ? b.order : 0;
                return orderA - orderB;
            });
            nodes.forEach(node => sortByOrder(node.children));
        };
        sortByOrder(rootPages);

        return rootPages;
    }, []);

    const tree = buildTree(pages);

    const toggleExpand = (pageId, event) => {
        event.stopPropagation();
        setExpandedNodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(pageId)) {
                newSet.delete(pageId);
            } else {
                newSet.add(pageId);
            }
            return newSet;
        });
    };

    const handleSelect = (page) => {
        if (onSelect) {
            onSelect(page);
        }
    };

    const renderNode = (node, depth = 0) => {
        const hasChildren = node.children && node.children.length > 0;
        const isExpanded = expandedNodes.has(node.pageId);
        const isSelected = selectedPageId === node.pageId;

        return (
            <div key={node.pageId}>
                <div
                    className={`
                        flex items-center px-3 py-2 cursor-pointer rounded transition-colours
                        ${isSelected
                            ? 'bg-primary-100 text-primary-900 font-medium'
                            : 'text-gray-700 hover:bg-gray-100'
                        }
                    `}
                    style={{ paddingLeft: `${depth * 16 + 12}px` }}
                    onClick={() => handleSelect(node)}
                >
                    <span
                        className="w-5 h-5 flex items-center justify-center flex-shrink-0"
                        onClick={(e) => hasChildren && toggleExpand(node.pageId, e)}
                    >
                        {hasChildren ? (
                            isExpanded ? (
                                <FaChevronDown className="w-3 h-3 text-gray-400" />
                            ) : (
                                <FaChevronRight className="w-3 h-3 text-gray-400" />
                            )
                        ) : (
                            <FaFile className="w-3 h-3 text-gray-300" />
                        )}
                    </span>
                    <span className="flex-1 text-sm truncate ml-1">
                        {node.title}
                    </span>
                </div>
                {hasChildren && isExpanded && (
                    <div>
                        {node.children.map(child => renderNode(child, depth + 1))}
                    </div>
                )}
            </div>
        );
    };

    if (tree.length === 0) {
        return (
            <p className="text-sm text-gray-500 py-4">No wiki pages available</p>
        );
    }

    return (
        <div className="space-y-0.5">
            {tree.map(node => renderNode(node, 0))}
        </div>
    );
};

export default WikiTreeNav;
